'use client';

import { InsertLeadToPageForm } from '@/app/InsertLeadToPageForm';
import { PageHeader } from '@/app/PageHeader';
import { insertLeadForPageOfPageCollection } from '@/data/leads';
import { PageCollectionConfigLanguage } from '@creator-suite/page-types';
import { useState } from 'react';
import { InsertLeadToPageFormSchemaType } from '@/data/leads-dto';
import { getPageCollectionByDomainSubdomainAndPath } from '@/data/page-collections';
import { TypeEnumIncentiveType } from '@creator-suite/enums';
import { NO_WHATSAPP_COMMUNITY_AVAILABLE_FOR_INCENTIVE_ERROR_NAME } from '@/data/whatsapp-communities-dto';

export interface IncentiveWithLinkProps {
  pageCollection: Omit<
    Awaited<ReturnType<typeof getPageCollectionByDomainSubdomainAndPath>>,
    'page' | 'incentive' | 'lottery'
  >;

  page: Awaited<
    ReturnType<typeof getPageCollectionByDomainSubdomainAndPath>
  >['page'];

  organizationLegalName: string;
  accountDisplayName: string;
  incentiveType: TypeEnumIncentiveType;

  slug: string;
  host: string;
  path: string;
  domain: string;
  language: PageCollectionConfigLanguage;
  countryCode?: string;
}

const handleFormError = (error: unknown) => {
  console.error(error);
};

export const IncentiveWithLink: React.FC<IncentiveWithLinkProps> = (props) => {
  const {
    slug,
    host,
    domain,
    path,
    language,
    pageCollection,
    page,
    organizationLegalName,
    accountDisplayName,
    incentiveType,
    countryCode,
  } = props;

  const [state, setState] = useState<
    'initial' | 'loading' | 'success' | 'error'
  >('initial');

  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleFormSubmit = async (
    data: InsertLeadToPageFormSchemaType,
  ): Promise<string> => {
    setState('loading');

    let result;

    try {
      result = await insertLeadForPageOfPageCollection({
        pageCollectionSlug: slug,
        path,
        data,
      });
    } catch (error: unknown) {
      // FIXME: Add Sentry handling.
      console.error(error);

      setState('error');
      setErrorMessage(
        'Leider ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      );

      throw error;
    }

    if (result?.validationErrors) {
      console.error('Validation errors:', result.validationErrors);
      setState('error');
      setErrorMessage('Bitte überprüfe deine Eingaben und versuche es erneut.');

      // TODO: Make this more sophisticated.
      throw new Error('Validation failed.');
    }

    if (result?.serverError) {
      const error = result.serverError;
      if (
        error.name === NO_WHATSAPP_COMMUNITY_AVAILABLE_FOR_INCENTIVE_ERROR_NAME
      ) {
        setState('error');
        setErrorMessage(
          'Die Community ist aktuell voll. Wenn wir neue Plätze öffnen, geben wir dir Bescheid!',
        );

        throw error;
      } else {
        console.error(`An unhandled error occurred.`);
        console.error(error);
        throw error;
      }
    }

    if (result?.data) {
      setState('success');
      return result.data.redirect;
    }

    throw new Error('No response from server.');
  };

  const { config } = pageCollection;

  return (
    <InsertLeadToPageForm
      language={language}
      accountDisplayName={accountDisplayName}
      organizationLegalName={organizationLegalName}
      handleSubmit={handleFormSubmit}
      onFormError={handleFormError}
      success={state === 'success'}
      fontFamily={config.fontFamily}
      title={page.title}
      description={page.description}
      buttonRadiusClassName={config.buttonRadiusClassName}
      buttonText={page.data.buttonText}
      fieldset={page.data.fieldset}
      cardRadiusClassName={config.cardRadiusClassName}
      inputRadiusClassName={config.inputRadiusClassName}
      error={errorMessage}
      incentiveType={incentiveType}
      externalPrivacyPolicyUrl={config.externalPrivacyPolicyUrl}
      countryCode={countryCode}
    >
      <PageHeader
        host={host}
        domain={domain}
        primaryColor={config.primaryColor}
        headerText={config.headerText}
        logoWidthPx={config.logoWidthPx ?? 0}
        logoImageUrl={config.logoUrl}
        title={page.title}
        fontFamily={config.fontFamily}
      >
        {page.description}
      </PageHeader>
    </InsertLeadToPageForm>
  );
};
